import { useEffect } from "react";
import { Navigate, Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { StatusCatalogueProvider } from "@/domains/pm/statuses/StatusCatalogueContext";
import { ScheduleChangeProvider } from "@/domains/pm/components/issues/schedule/ScheduleChangeProvider";
import {
  Database,
  FileText,
  Gauge,
  ListTodo,
  Box,
  DraftingCompass,
  UserCheck,
  Bell,
  PenLine,
  ClipboardList,
  Contact,
} from "lucide-react";
import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { PageLayout } from "./PageLayout";
import { buildingBasePath, isBuildingOnlyPath, isProjectsOnlyPath } from "./buildingViewRoutes";
import { Button } from "@/components/ui/button";
import { ProjectsEmptyState } from "@/domains/projects/components/ProjectsEmptyState";
import { CreateProjectDialogProvider } from "@/domains/projects/components/CreateProjectDialogProvider";
import { ProjectScopeChooser } from "@/components/building/ProjectScopeChooser";
import { NavigationSidebar } from "@/components/navigation";
import type { NavItem } from "@/components/navigation";
import { useSidebarState } from "@/components/navigation/hooks/useSidebarState";
import { SidebarBottomMenus } from "@/components/navigation/organisms/SidebarBottomMenus";
import { SidebarViewModeToggle } from "@/components/navigation/molecules/SidebarViewModeToggle";
import { SidebarChat } from "@/components/chat/SidebarChat";
import * as pmRepo from "@/stores/pm/repository";
import { createCoalescer } from "@/lib/realtime/coalesce";
import { useDocumentsRealtimeBridge } from "@/domains/documents/hooks/useDocumentsRealtimeBridge";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useBuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import {
  PROJECT_FEATURE_DENIED_ACTION,
  useProjectFeatureAccess,
  type ProjectFeature,
} from "@/hooks/useProjectFeatureAccess";
import { permissionDeniedMessage } from "@/lib/permissionDenied";
import { useSigningVisible } from "@/domains/signing/useSigningAccess";
import { PERMISSIONS, type Permission } from "@/constants/permissions";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";
import {
  useIssuesStore,
  useMentionsStore,
  useNotificationsStore,
} from "@/stores/pm/hooks/usePMStore";
import { StorageUploadFeedback } from "@/domains/documents/components/StorageUploadFeedback";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizeAwaitingReview } from "@/domains/documents/hooks/useOrganizeAwaitingReview";
import { useBriefOpenFindingsCount } from "@/domains/brief/hooks/useBriefOpenFindingsCount";
import { useBriefBuildingRealtime } from "@/domains/brief/hooks/useBriefRealtime";
import { useBriefSchemaAvailable } from "@/domains/brief/hooks/useBriefSchema";

const PM_NAV_ITEM_IDS = new Set(["pm-section", "issues", "my-issues", "notifications"]);

/** The project feature a nav item opens, if per-project permissions can refuse it. */
function navItemFeature(id: string): ProjectFeature | null {
  if (id === "drawing-compare") return "drawingCompare";
  if (id === "contacts") return "contacts";
  if (id === "model-3d") return "modelViewer";
  // The section label is a heading, not a page: it stays as it is.
  if (id === "issues" || id === "my-issues" || id === "notifications") return "issues";
  return null;
}

/** Coalescing window for the nav dots' count queries. Matches the Notifications
 *  feed's, since both are driven by the same burst of activity rows. */
const DOT_REFRESH_WINDOW_MS = 750;

// Which tabs each sidebar view mode shows. The Files page is shared (its
// Folders tab is Projects-view-only, handled inside the page); the dashboards
// differ, and the 3D model + PM section are Projects-only.
// Contacts is in both: per project in Projects view, grouped by project in
// Building view.
const BUILDING_VIEW_TAB_IDS = new Set(["building", "building-data", "documents", "contacts"]);
const PROJECTS_VIEW_TAB_IDS = new Set([
  "project-dashboard",
  "documents",
  "models-section",
  "model-3d",
  "drawing-compare",
  // Signing is Projects-only: a chain is started from a project's files and its
  // container is filed back into that project, so the page has a project to
  // narrow to and no meaningful whole-building reading.
  "signing",
  // The brief (lähteülesanne) is one project's requirements.
  "brief",
  "contacts",
  ...PM_NAV_ITEM_IDS,
]);

/**
 * BuildingLayout - Container component for all building pages
 *
 * Provides a consistent layout wrapper with:
 * - NavigationSidebar for building navigation (tabs filtered by permission)
 * - React Router Outlet for child route content
 * - SidebarChat (Franklin) for AI assistance
 */
export const BuildingLayout = observer(function BuildingLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const buildingStore = useBuildingStore();
  const perms = usePermissions();
  const mentionsStore = useMentionsStore();
  const issuesStore = useIssuesStore();

  // Document parent issues are titled in the viewer's language from data
  // already on the row; a language switch re-derives them without a refetch.
  useEffect(() => {
    const onLanguage = () => issuesStore.relocaliseTitles();
    window.addEventListener("franklin:language-changed", onLanguage);
    return () => window.removeEventListener("franklin:language-changed", onLanguage);
  }, [issuesStore]);
  const notificationsStore = useNotificationsStore();
  const { userProfile } = useAuth();
  const { currentOrganization, isReady: orgReady } = useOrganizationContext();
  const signingVisible = useSigningVisible();

  const buildingId = id || "";
  const buildingIdNum = Number(buildingId);

  // WHA-3654: red dot on the Files nav item while one of the user's AI
  // organise proposals awaits review; clears when it is applied/discarded.
  const { awaitingReviewCount } = useOrganizeAwaitingReview(
    Number.isNaN(buildingIdNum) || buildingIdNum === 0 ? null : buildingIdNum,
    userProfile?.id ?? null
  );

  // Global building scope — drives the nav unread dots (mentions +
  // notifications count only the scoped project's issue activity) and the
  // Projects-view project gate above the page.
  const {
    selectedProjectId,
    setProject,
    projectOptions,
    optionsLoaded,
    optionsError,
    reloadOptions,
  } = useBuildingScope(buildingId);
  // Issues and Drawing compare stay in a member's sidebar when their project
  // permissions shut them out, dimmed, so the refusal can be explained rather
  // than the item silently vanishing; ProjectPermissionRouteGuard answers the
  // same question for a typed URL.
  const featureAccess = useProjectFeatureAccess({
    selectedProjectId,
    projectOptions,
    optionsLoaded,
  });
  const denyPermission = usePermissionDenied();

  // Sidebar "Building / Projects" view mode — remembered per building. Always
  // "projects" for a subcontractor-only member.
  const { viewMode, setViewMode } = useBuildingViewMode(buildingId);

  // A subcontractor-only member has no Building view, so the building-only
  // routes send them to the Files page. Done here rather than only by the
  // routes' SubcontractorRedirect: in their forced Projects view the project
  // gate below can stand in for the Outlet (no project resolved), and the
  // route-level redirect would never mount.
  const leaveBuildingOnlyPath =
    perms.isSubcontractorOnly && isBuildingOnlyPath(location.pathname, buildingId);

  // Deep-link rule: route wins, mode follows. A projects-only route (PM,
  // 3D model, project dashboard) flips the sidebar to Projects view; the
  // building dashboard index flips it to Building view. Shared routes
  // (the Files page, edit) never flip the mode.
  useEffect(() => {
    if (!buildingId) return;
    if (viewMode === "building" && isProjectsOnlyPath(location.pathname, buildingId)) {
      setViewMode("projects");
    } else if (viewMode === "projects" && isBuildingOnlyPath(location.pathname, buildingId)) {
      setViewMode("building");
    }
  }, [buildingId, viewMode, location.pathname, setViewMode]);

  useEffect(() => {
    if (buildingId) {
      buildingStore.setLastVisitedBuildingId(buildingId);
    }
  }, [buildingId, buildingStore]);

  // Every building-scoped consumer that counts documents — the dashboard
  // donut, the upload charts, "Uploaded today", the project activity cards —
  // renders under this layout, so one subscription here keeps the lot in step
  // with the pipeline instead of each page growing its own.
  //
  // Its own channel, separate from DocumentStore's: the store holds a single
  // channel slot claimed by whichever document surface mounted last, so a
  // third claimant would evict the second (see the hook's docblock).
  useDocumentsRealtimeBridge(buildingId);

  // Brief check runs and findings for the whole building: the nav dot below
  // and the "Franklin finished checking" toast, from whichever page is open.
  // Both wait on the schema probe: before the brief migration applies, the
  // channel would error and re-join forever and the count would 404 twice a
  // minute for every owner and admin.
  const { available: briefSchemaAvailable } = useBriefSchemaAvailable(perms.canViewBriefs);
  const briefReady = perms.canViewBriefs && briefSchemaAvailable === true;
  useBriefBuildingRealtime(briefReady ? buildingId : null);
  const { openFindingsCount: briefOpenCount } = useBriefOpenFindingsCount(
    Number.isNaN(buildingIdNum) || buildingIdNum === 0 ? null : buildingIdNum,
    selectedProjectId,
    briefReady
  );

  // WHA-3339 / WHA-3733: what the two nav dots count is scoped to the building
  // AND the selected project, so a scope change re-reads both counts. Kept
  // apart from the subscription below, whose channel is building-scoped only.
  useEffect(() => {
    if (!buildingIdNum || !userProfile?.id) return;
    mentionsStore.setContext(buildingIdNum, userProfile.id, selectedProjectId);
    mentionsStore.refresh();
    mentionsStore.refreshDocuments();
    notificationsStore.setContext(buildingIdNum, userProfile.id, selectedProjectId);
    notificationsStore.refresh();
  }, [buildingIdNum, userProfile?.id, selectedProjectId, mentionsStore, notificationsStore]);

  // Keep both dots fresh: the existing chatbot write event, the local read
  // event IssuesStore dispatches when the user opens an issue, and the
  // building's activity log.
  //
  // Deliberately NOT keyed on `selectedProjectId`: the topic is
  // `pm_issue_activity:<buildingId>`, so re-running on a scope change would
  // tear down and immediately recreate the same topic while removeChannel is
  // still in flight — the collision `channel.ts` warns about. The effect above
  // owns the scope; this one owns the socket.
  useEffect(() => {
    if (!buildingIdNum || !userProfile?.id) return;

    // One issue save writes several activity rows (the triggers log a
    // remove+add for an assignee that did not change), so the raw stream is
    // coalesced into one pair of count queries per burst.
    const refreshDots = createCoalescer(() => {
      mentionsStore.refresh();
      notificationsStore.refresh();
    }, DOT_REFRESH_WINDOW_MS);

    const onChange = () => refreshDots.call();
    const onRead = (e: Event) => {
      const detail = (e as CustomEvent<{ count?: number }>).detail;
      if (detail?.count) mentionsStore.decrement(detail.count);
      else mentionsStore.refresh();
    };
    const onNotificationsSeen = () => {
      notificationsStore.refresh();
    };
    // Document comment threads mark their @mentions read as they render; the
    // Documents nav dot re-reads its count on that signal. Creation of new
    // mentions reaches the store through the comments realtime bridge.
    const onDocumentMentionsRead = () => {
      mentionsStore.refreshDocuments();
    };
    window.addEventListener("franklin:pm-issue-changed", onChange);
    window.addEventListener("franklin:pm-mentions-read", onRead);
    window.addEventListener("franklin:pm-notifications-seen", onNotificationsSeen);
    window.addEventListener("franklin:document-mentions-read", onDocumentMentionsRead);

    // `franklin:pm-issue-changed` is dispatched from exactly one place — the
    // chatbot store — so until now a teammate creating, assigning or
    // commenting on an issue left both dots dark until you navigated away and
    // back. `pm_issue_activity` is what the dots count, and it was published
    // for realtime with nobody listening.
    const activity = pmRepo.subscribeToActivity(buildingIdNum, onChange);

    return () => {
      window.removeEventListener("franklin:pm-issue-changed", onChange);
      window.removeEventListener("franklin:pm-mentions-read", onRead);
      window.removeEventListener("franklin:pm-notifications-seen", onNotificationsSeen);
      window.removeEventListener("franklin:document-mentions-read", onDocumentMentionsRead);
      refreshDots.cancel();
      activity.unsubscribe();
    };
  }, [buildingIdNum, userProfile?.id, mentionsStore, notificationsStore]);

  const {
    detail: building,
    currentLookupFailure,
    isCurrentLookupComplete,
  } = useBuildingDetail(buildingId);

  // WHA-3109: Lift sidebar collapse state up so SidebarChat can react to it.
  const {
    isCollapsed: isNavCollapsed,
    toggleCollapse: toggleNavCollapse,
    skipTransition: skipNavTransition,
  } = useSidebarState({
    buildingId,
    defaultCollapsed: false,
  });

  // PM module is gated behind a PostHog flag; default on so this is a no-op
  // when PostHog is unreachable or the flag is absent.
  const pmModuleEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);

  // Each tab is gated by a permission. Sub-only users see just the subset
  // their tab permissions grant. Non-sub users see all tabs — existing roles
  // don't yet hold every tab permission (rolled in per-role in future specs)
  // so we fall back to the full tab set for them until that cleanup happens.
  // A tab with no `permission` is ungated — shown to everyone, subs included
  // (e.g. the 3D model viewer, whose document scope is enforced by RLS).
  // An array of permissions is "any-of" — holding one of them shows the tab.
  //
  // Files tab: VIEW_DOCUMENTS_LIST_TIMELINE covers the Timeline/List tabs and
  // BUILDING_VIEW_PROJECTS the Folders tab inside the page (the removed
  // /folders page's grant) — either grant warrants the nav entry for
  // subcontractors. BUILDING_VIEW_DOCUMENTS handles the row-level RLS side.
  const ALL_TABS: Array<NavItem & { permission?: Permission | Permission[] }> = [
    {
      id: "building",
      label: t.buildings.sidebar.dashboard,
      // Same icon as the Projects-view dashboard tab — both are "the
      // dashboard" of their view; the toggle already carries the
      // building-vs-projects distinction.
      icon: Gauge,
      href: `/buildings/${buildingId}`,
      isActive: location.pathname === `/buildings/${buildingId}`,
      permission: PERMISSIONS.BUILDING_VIEW_DASHBOARD,
    },
    {
      id: "project-dashboard",
      label: t.buildings.sidebar.dashboard,
      icon: Gauge,
      href: `/buildings/${buildingId}/project-dashboard`,
      isActive: location.pathname === `/buildings/${buildingId}/project-dashboard`,
      permission: PERMISSIONS.BUILDING_VIEW_DASHBOARD,
    },
    {
      id: "documents",
      label: t.buildings.sidebar.documents,
      icon: FileText,
      href: `/buildings/${buildingId}/documents`,
      // Exact match: the Files page has no sub-routes, and a prefix match would
      // also light up on the legacy `/documents-log` path before its redirect fires.
      isActive: location.pathname === `/buildings/${buildingId}/documents`,
      permission: [PERMISSIONS.VIEW_DOCUMENTS_LIST_TIMELINE, PERMISSIONS.BUILDING_VIEW_PROJECTS],
      // WHA-3654: an AI organise proposal awaits the user's review. Projects
      // view only — the Folders tab that owns those proposals is itself
      // Projects-view-only, so in Building view the dot would point at a page
      // that cannot show the thing it is announcing.
      // … or someone @mentioned the viewer in a document comment thread.
      showUnreadDot:
        (viewMode === "projects" && awaitingReviewCount > 0) ||
        mentionsStore.documentUnreadCount > 0,
    },
    {
      // The project brief (lähteülesanne): its documents, Franklin's findings
      // and the confirmed versions. Projects view only; gated below on
      // VIEW_BRIEFS with its own branch (a `permission` field alone would show
      // it to every owner/admin regardless of grant).
      id: "brief",
      label: t.brief.sidebar,
      icon: ClipboardList,
      href: `/buildings/${buildingId}/brief`,
      isActive: location.pathname === `/buildings/${buildingId}/brief`,
      // Open findings await a decision on the selected project's brief.
      showUnreadDot: viewMode === "projects" && briefOpenCount > 0,
    },
    {
      // Project phone book: members plus outside contacts. Below Brief and
      // above Signing in Projects view; after Documents in Building view.
      id: "contacts",
      label: t.contacts.sidebar,
      icon: Contact,
      href: `/buildings/${buildingId}/contacts`,
      isActive: location.pathname === `/buildings/${buildingId}/contacts`,
    },
    {
      // Signed .asice containers + every signing chain the viewer can see, for
      // the selected project (`signing_sessions.project_id` /
      // `documents.project_id`, populated since FDP 20260731103000).
      id: "signing",
      label: t.signing.sidebar,
      icon: PenLine,
      href: `/buildings/${buildingId}/signing`,
      isActive: location.pathname === `/buildings/${buildingId}/signing`,
    },
    {
      // Sits below Documents in the Building view (deliberate order).
      id: "building-data",
      label: t.buildings.sidebar.buildingData,
      icon: Database,
      href: `/buildings/${buildingId}/data`,
      isActive: location.pathname === `/buildings/${buildingId}/data`,
      // Gated below via canViewBuildingDataSidebar (building-scoped, with a
      // platform-admin bypass) — not the generic permission filter, which
      // only binds for subcontractor-only users.
      permission: PERMISSIONS.VIEW_BUILDING_DATA_SIDEBAR,
    },
    {
      // Models section — the 3D (IFC) viewer and the DWG Drawing Compare.
      // The 3D viewer is ungated (open to all org members, documents
      // RLS-scoped); Drawing Compare gates on VIEW_DRAWING_COMPARE_TOOL,
      // matching its PermissionProtectedRoute.
      id: "models-section",
      label: t.buildings.sidebar.modelsSection,
      isSectionLabel: true,
    },
    {
      id: "model-3d",
      label: t.buildings.sidebar.model3d,
      icon: Box,
      href: `/buildings/${buildingId}/model`,
      isActive: location.pathname.endsWith("/model"),
    },
    {
      id: "drawing-compare",
      label: t.tools.drawingCompare.navLabel,
      icon: DraftingCompass,
      href: `/buildings/${buildingId}/drawings`,
      isActive: location.pathname.endsWith("/drawings"),
    },
    {
      id: "pm-section",
      label: t.pm.sidebar.sectionLabel,
      isSectionLabel: true,
      permission: PERMISSIONS.VIEW_PM_TOOL,
    },
    {
      id: "issues",
      label: t.pm.sidebar.issues,
      icon: ListTodo,
      href: `/buildings/${buildingId}/issues`,
      isActive: location.pathname.endsWith("/issues"),
      permission: PERMISSIONS.VIEW_PM_TOOL,
    },
    {
      id: "my-issues",
      label: t.pm.sidebar.myIssues,
      icon: UserCheck,
      href: `/buildings/${buildingId}/my-issues`,
      isActive: location.pathname.endsWith("/my-issues"),
      permission: PERMISSIONS.VIEW_PM_TOOL,
      // WHA-3644: unread @-mentions surface in the My Issues → Mentions tab,
      // so the nav dot lives here now (moved off the Issues item).
      showUnreadDot: mentionsStore.unreadCount > 0,
    },
    {
      id: "notifications",
      label: t.pm.sidebar.notifications,
      icon: Bell,
      href: `/buildings/${buildingId}/notifications`,
      isActive: location.pathname.endsWith("/notifications"),
      permission: PERMISSIONS.VIEW_PM_TOOL,
      // WHA-3733: lights up when there's issue activity the user hasn't seen.
      showUnreadDot: notificationsStore.unreadCount > 0,
    },
  ];

  // Mode partition first (which tabs this view mode shows at all), then the
  // existing permission / feature-flag / subcontractor filters.
  const modeTabIds = viewMode === "projects" ? PROJECTS_VIEW_TAB_IDS : BUILDING_VIEW_TAB_IDS;

  const navItems: NavItem[] = ALL_TABS.filter((tab) => modeTabIds.has(tab.id))
    .filter((tab) => {
      // Drawing Compare mirrors its PermissionProtectedRoute. This needs its
      // own branch rather than a `permission` field on the tab: the generic
      // filter below only consults tab.permission for subcontractor-only
      // users, so a field alone would show the item to every org owner and
      // admin whether or not they hold the grant. Same reason building-data
      // and the PM items have explicit branches.
      if (tab.id === "drawing-compare") {
        return perms.canViewDrawingCompareTool;
      }
      // The project dashboard is everyone's, members included — its widgets are
      // each scoped by RLS or their own permission check. Explicit, because the
      // tab's BUILDING_VIEW_DASHBOARD (which no role holds) would otherwise hide
      // it from members through the generic filter below.
      if (tab.id === "project-dashboard") {
        return true;
      }
      // PM section/items show when the user holds VIEW_PM_TOOL (granted to
      // org_owner + subcontractor in WHA-3301). Per-issue access is enforced by
      // the project-scoped VIEW_PM / MANAGE_PM RLS policies.
      if (PM_NAV_ITEM_IDS.has(tab.id)) {
        return perms.canViewPMTool;
      }
      // Building Data page mirrors the old sidebar's gate exactly for ALL
      // users (platform-admin bypass + building-scoped grant) — the generic
      // filter below would show it to every non-subcontractor (WHA-3488).
      if (tab.id === "building-data") {
        return perms.canViewBuildingDataSidebar(buildingIdNum);
      }
      // Signing tab: the org's signing_enabled flag, platform admins always
      // (see useSigningVisible). Never the ALLOW_SIGNING permission — an invited
      // signer may hold no permission to start a signing and still has to reach
      // this page to finish the chain they were invited to. The flag alone here,
      // with no "…unless they have an open chain" fallback like the page's: that
      // would cost every building page a signing query to draw one tab, and a
      // signer in a switched-off org arrives by the turn letter's direct link.
      if (tab.id === "signing") {
        return signingVisible;
      }
      // Brief: VIEW_BRIEFS held anywhere in the org shows the tab (owners and
      // admins bypass) once the schema probe has confirmed the tables exist;
      // per-project access is the brief_* RLS policies.
      if (tab.id === "brief") {
        return briefReady;
      }
      // Contacts follows the same building/project assignment check as its page.
      // A member with projects here keeps the tab even when contacts are off in
      // the current project: it is dimmed below, so the refusal is explained.
      // It stays hidden while access is loading (no dimmed flash) and for a
      // member with no project in this building at all, or whose role has no
      // contacts.
      if (tab.id === "contacts") {
        if (
          perms.isSubcontractorOnly &&
          featureAccess.ready &&
          projectOptions.length > 0 &&
          // The role grants contacts at all: without it (contacts not
          // installed for this org yet) there is nothing to dim towards.
          perms.hasPermission(PERMISSIONS.VIEW_CONTACTS)
        ) {
          return true;
        }
        return perms.canViewContacts({
          buildingId: buildingIdNum,
          projectId: selectedProjectId,
          buildingOrgId:
            Number(building?.base.id) === buildingIdNum ? building.base.org_id : undefined,
        });
      }
      // Ungated tabs (no permission) are visible to everyone, subcontractors
      // included — document/row access inside is enforced by RLS.
      if (!tab.permission) {
        return true;
      }
      if (!perms.isSubcontractorOnly) {
        return true;
      }
      const required = Array.isArray(tab.permission) ? tab.permission : [tab.permission];
      return required.some((permission) =>
        perms.hasPermission(permission, { buildingId: buildingIdNum })
      );
    })
    .filter((tab) => pmModuleEnabled || !PM_NAV_ITEM_IDS.has(tab.id))
    .map(({ permission: _permission, ...rest }): NavItem => {
      const feature = navItemFeature(rest.id);
      if (!feature || featureAccess.allowed(feature)) return rest;
      const action = PROJECT_FEATURE_DENIED_ACTION[feature];
      return {
        ...rest,
        isActive: false,
        restricted: {
          reason: permissionDeniedMessage(
            t.common.permissions,
            action,
            featureAccess.projectId === null ? "anyProject" : "project"
          ).title,
          onActivate: () => denyPermission(action, featureAccess.projectId),
        },
      };
    });

  // Wait for this mount's lookup before showing a subcontractor any building
  // surface. A cached error for the same id may predate a newly granted access.
  if (perms.isSubcontractorOnly && !isCurrentLookupComplete) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="building-access-pending">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A denied building has no project options. Without this guard the Projects
  // view gate below replaces the route with "Create your first project" before
  // its child redirect can run. Only an explicit denial from this lookup
  // redirects; network failures stay on the existing error path.
  if (
    perms.isSubcontractorOnly &&
    (currentLookupFailure === "not_found" || currentLookupFailure === "forbidden")
  ) {
    return <Navigate to="/buildings" replace />;
  }

  return (
    <PageLayout showHeader={false} customPadding>
      {/* One status catalogue for every PM surface under this building. The
          badges are rendered from a dozen places that hold an issue and no
          building id, so it is a context rather than a prop. */}
      <StatusCatalogueProvider
        buildingId={Number.isFinite(buildingIdNum) ? buildingIdNum : undefined}
      >
        {/* Every date control under a building writes through one cascade, so
            the same edit behaves the same in the list, the panel, the context
            menu and the Gantt. */}
        <ScheduleChangeProvider buildingId={buildingIdNum}>
          {/*
           * One create-project dialog for the whole building page, opened from
           * the sidebar picker's "New project". The follow-through is this
           * layout's `setProject`: the layout is mounted for the whole building
           * page, so a create resolving after the picker's popover has closed
           * still lands against the live location instead of a frozen one.
           */}
          <CreateProjectDialogProvider buildingId={buildingId} onProjectCreated={setProject}>
            <div className="flex h-screen w-full">
              <StorageUploadFeedback buildingId={buildingId} />
              <NavigationSidebar
                buildingId={buildingId}
                items={navItems}
                logo={
                  <FranklinLogo
                    size="sm"
                    expanded={!isNavCollapsed}
                    showBorder={false}
                    onClick={() => navigate("/buildings")}
                    className="bg-transparent"
                  />
                }
                // The building and project heading — the only place either is
                // named; pages carry no context line above them. A
                // subcontractor-only member has Projects view alone (see
                // useIsProjectScopedMember), so theirs has no Building choice.
                headerSlot={({ isCollapsed }) => (
                  <SidebarViewModeToggle
                    buildingId={buildingId}
                    isCollapsed={isCollapsed}
                    buildingName={building?.base.name}
                    projectsOnly={perms.isSubcontractorOnly}
                  />
                )}
                footer={({ isCollapsed }) => <SidebarBottomMenus isCollapsed={isCollapsed} />}
                isCollapsed={isNavCollapsed}
                onToggleCollapse={toggleNavCollapse}
                skipTransition={skipNavTransition}
              />
              {/*
               * No bottom padding here on purpose. This column is a 100vh flex child,
               * so padding on it shortens every page that owns an inner scroller and
               * the reserved band becomes dead space the list can never reach — the
               * blank footer that used to cut off the field list, the issues list and
               * the documents table. The chat pill's footprint is reserved by each
               * scrolling page instead, as trailing space INSIDE its scroller
               * (`CHAT_LAUNCHER_RESERVE` in `chat/SidebarChat/chatWidgetLayout.ts`).
               */}
              <div data-sidebar-content className="flex min-w-0 flex-1 flex-col overflow-auto">
                {/*
                 * Projects view always works within one concrete project, so every
                 * projects-mode route (including the headerless /model) sits behind
                 * this gate until the scope resolves: load failure → retry; still
                 * loading → spinner; no projects at all → create-first empty state;
                 * projects exist but none chosen → select-a-project prompt. Normal
                 * first-time entry never reaches that last branch — the sidebar
                 * toggle's dropdown collects the choice before the mode flips — so
                 * it only shows on deep links into a projects-only route (or when
                 * the remembered project disappeared). Picking persists via
                 * setProject, so returning users go straight through. Building view
                 * is untouched.
                 */}
                {leaveBuildingOnlyPath ? (
                  <Navigate
                    to={`${buildingBasePath(buildingId)}/documents${location.search}`}
                    replace
                  />
                ) : viewMode !== "projects" || selectedProjectId != null ? (
                  <Outlet />
                ) : !optionsLoaded && optionsError ? (
                  <div className="flex h-64 flex-col items-center justify-center gap-3">
                    <p className="text-sm text-muted-foreground">{t.buildings.scope.loadFailed}</p>
                    <Button variant="outline" size="sm" onClick={reloadOptions}>
                      {t.common.retry}
                    </Button>
                  </div>
                ) : !optionsLoaded ? (
                  <div className="flex h-64 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : projectOptions.length === 0 && perms.isSubcontractorOnly ? (
                  // They cannot create one, so "create your first project" would
                  // be the wrong answer: they are simply not on a project here.
                  <div
                    className="flex min-h-64 flex-col items-center justify-center gap-1 p-6 text-center"
                    data-testid="no-project-access"
                  >
                    <h2 className="text-lg font-semibold">{t.projects.chooser.noAccessTitle}</h2>
                    <p className="text-sm text-muted-foreground">
                      {t.projects.chooser.noAccessBody}
                    </p>
                  </div>
                ) : projectOptions.length === 0 ? (
                  <div className="p-6">
                    <ProjectsEmptyState buildingId={buildingId} />
                  </div>
                ) : (
                  /*
                   * The gate keeps its own picker: this branch is only reached by a
                   * deep link whose scope never resolved, and the answer belongs
                   * next to the question rather than behind a sidebar control the
                   * prompt doesn't point at. Not a scope *switcher* (that stays in
                   * the sidebar) — it resolves an
                   * unresolved scope, which is what the prompt asks for.
                   */
                  <ProjectScopeChooser
                    buildingId={buildingId}
                    projects={projectOptions}
                    onSelect={setProject}
                  />
                )}
              </div>
            </div>
            {buildingId && (
              <SidebarChat buildingId={buildingId} isSidebarCollapsed={isNavCollapsed} />
            )}
          </CreateProjectDialogProvider>
        </ScheduleChangeProvider>
      </StatusCatalogueProvider>
    </PageLayout>
  );
});
